/**
 * @engine/core - ComponentRegistry
 * 
 * Registry for component types. Maps TypeScript component
 * definitions to AssemblyScript runtime via embex.
 */

import type { ComponentId, GUID } from './types';

/**
 * Field type for component properties
 */
export type FieldType = 
  | 'bool'
  | 'i8' | 'i16' | 'i32' | 'i64'
  | 'u8' | 'u16' | 'u32' | 'u64'
  | 'f32' | 'f64'
  | 'string'
  | 'vec2' | 'vec3' | 'vec4'
  | 'color' | 'color32'
  | 'entity' | 'asset'
  | 'array' | 'object';

/**
 * Field definition
 */
export interface FieldDef {
  name: string;
  type: FieldType;
  default?: unknown;
  /** For arrays/objects: element type */
  elementType?: FieldType;
  /** For asset references: asset type filter */
  assetType?: string;
  /** Editor hints */
  editor?: {
    hidden?: boolean;
    readonly?: boolean;
    label?: string;
    tooltip?: string;
    min?: number;
    max?: number;
    step?: number;
    group?: string;
    /** Inspector widget override; if omitted, inferred from `type` + hints. */
    widget?:
      | "slider"
      | "dropdown"
      | "enum"
      | "color"
      | "asset"
      | "objectRef"
      | "vector"
      | "toggle"
      | "text"
      | "number";
    /** Choices for dropdown/enum widgets. */
    options?: Array<{ label: string; value: unknown }>;
  };
}

/**
 * Component type definition
 */
export interface ComponentDef {
  /** Unique type ID (e.g., 'core.Transform', 'game.Player') */
  typeId: string;
  /** Display name for editor */
  displayName: string;
  /** Component fields */
  fields: FieldDef[];
  /** Category for editor grouping */
  category?: string;
  /** Icon for editor UI (Lucide icon name) */
  icon?: string;
  /** Color for editor UI (hex string) */
  color?: string;
  /**
   * Gizmo icon for scene view rendering (Lucide icon name).
   * Rendered at component's transform position with 50% opacity.
   * If not specified, falls back to `icon` property.
   * Example: 'Video', 'Image', 'Volume2'
   */
  gizmoIcon?: string;
  /** Allow multiple instances on one GameObject */
  allowMultiple?: boolean;
  /** Required components (auto-added) */
  requires?: string[];
  /** Execution order (lower = earlier) */
  executionOrder?: number;
  /** Execute component logic in editor mode (e.g., rendering) */
  executeInEditor?: boolean;
  /** Runtime-only property names (`@runtime`) — excluded from serialization. */
  runtimeFields?: ReadonlySet<string>;
}

/**
 * Component instance data
 */
export interface ComponentData {
  typeId: string;
  values: Record<string, unknown>;
}

/**
 * ComponentRegistry - Manages component type definitions
 * 
 * Responsibilities:
 * - Store component type definitions
 * - Provide component metadata for inspector
 * - Create/destroy component instances (via embex)
 * - Serialize/deserialize component data
 */
export class ComponentRegistry {
  // Singleton
  private static _instance: ComponentRegistry | null = null;
  
  // Type definitions
  private _types: Map<string, ComponentDef> = new Map();
  private _typesByCategory: Map<string, string[]> = new Map();
  
  // Instance data (maps componentId -> data)
  private _instances: Map<ComponentId, ComponentData> = new Map();
  
  private constructor() {}
  
  /**
   * Get singleton instance
   */
  static get instance(): ComponentRegistry {
    if (!ComponentRegistry._instance) {
      ComponentRegistry._instance = new ComponentRegistry();
    }
    return ComponentRegistry._instance;
  }
  
  /**
   * Reset singleton (for testing)
   */
  static reset(): void {
    ComponentRegistry._instance = null;
  }
  
  // ==========================================================================
  // TYPE REGISTRATION
  // ==========================================================================
  
  /**
   * Register a component type
   */
  registerType(def: ComponentDef): void {
    if (this._types.has(def.typeId)) {
      console.warn(`Component type '${def.typeId}' already registered`);
      return;
    }
    
    this._types.set(def.typeId, def);
    
    // Index by category
    const category = def.category ?? 'General';
    if (!this._typesByCategory.has(category)) {
      this._typesByCategory.set(category, []);
    }
    this._typesByCategory.get(category)!.push(def.typeId);
  }
  
  /**
   * Register multiple types
   */
  registerTypes(defs: ComponentDef[]): void {
    for (const def of defs) {
      this.registerType(def);
    }
  }
  
  /**
   * Unregister a component type
   */
  unregisterType(typeId: string): void {
    const def = this._types.get(typeId);
    if (!def) return;
    
    // Remove from category index
    const category = def.category ?? 'General';
    const categoryTypes = this._typesByCategory.get(category);
    if (categoryTypes) {
      const index = categoryTypes.indexOf(typeId);
      if (index !== -1) categoryTypes.splice(index, 1);
      if (categoryTypes.length === 0) this._typesByCategory.delete(category);
    }
    
    this._types.delete(typeId);
  }
  
  // ==========================================================================
  // TYPE LOOKUP
  // ==========================================================================
  
  /**
   * Get component type definition
   */
  getType(typeId: string): ComponentDef | null {
    return this._types.get(typeId) ?? null;
  }
  
  /**
   * Check if type is registered
   */
  hasType(typeId: string): boolean {
    return this._types.has(typeId);
  }
  
  /**
   * Get all registered type IDs
   */
  getAllTypeIds(): string[] {
    return Array.from(this._types.keys());
  }
  
  /**
   * Get all type definitions
   */
  getAllTypes(): ComponentDef[] {
    return Array.from(this._types.values());
  }
  
  /**
   * Get types in category
   */
  getTypesByCategory(category: string): ComponentDef[] {
    const typeIds = this._typesByCategory.get(category) ?? [];
    return typeIds.map(id => this._types.get(id)!).filter(Boolean);
  }
  
  /**
   * Get all categories
   */
  getCategories(): string[] {
    return Array.from(this._typesByCategory.keys());
  }
  
  // ==========================================================================
  // INSTANCE MANAGEMENT
  // ==========================================================================
  
  /**
   * Create component instance
   */
  createInstance(componentId: ComponentId, typeId: string, initialValues?: Record<string, unknown>): boolean {
    const def = this._types.get(typeId);
    if (!def) {
      console.error(`Unknown component type: ${typeId}`);
      return false;
    }
    
    // Build default values
    const values: Record<string, unknown> = {};
    for (const field of def.fields) {
      values[field.name] = initialValues?.[field.name] ?? field.default ?? this.getDefaultForType(field.type);
    }
    
    this._instances.set(componentId, { typeId, values });
    
    // TODO: Create in embex runtime
    // embex.createComponent(componentId, typeId, values);
    
    return true;
  }
  
  /**
   * Destroy component instance
   */
  destroyInstance(componentId: ComponentId): void {
    // TODO: Destroy in embex runtime
    // embex.destroyComponent(componentId);
    
    this._instances.delete(componentId);
  }
  
  /**
   * Get component instance data
   */
  getInstance(componentId: ComponentId): ComponentData | null {
    return this._instances.get(componentId) ?? null;
  }
  
  /**
   * Get component field value
   */
  getValue(componentId: ComponentId, fieldName: string): unknown {
    const instance = this._instances.get(componentId);
    return instance?.values[fieldName];
  }
  
  /**
   * Set component field value
   */
  setValue(componentId: ComponentId, fieldName: string, value: unknown): boolean {
    const instance = this._instances.get(componentId);
    if (!instance) return false;
    
    instance.values[fieldName] = value;
    
    // TODO: Sync to embex runtime
    // embex.setComponentValue(componentId, fieldName, value);
    
    return true;
  }
  
  /**
   * Set multiple field values
   */
  setValues(componentId: ComponentId, values: Record<string, unknown>): boolean {
    const instance = this._instances.get(componentId);
    if (!instance) return false;
    
    Object.assign(instance.values, values);
    
    // TODO: Batch sync to embex runtime
    // embex.setComponentValues(componentId, values);
    
    return true;
  }
  
  // ==========================================================================
  // SERIALIZATION
  // ==========================================================================
  
  /**
   * Serialize component to data
   */
  serializeInstance(componentId: ComponentId): ComponentData | null {
    return this._instances.get(componentId) ?? null;
  }
  
  /**
   * Deserialize component from data
   */
  deserializeInstance(componentId: ComponentId, data: ComponentData): boolean {
    if (!this._types.has(data.typeId)) {
      console.error(`Unknown component type: ${data.typeId}`);
      return false;
    }
    
    this._instances.set(componentId, { ...data });
    
    // TODO: Create in embex runtime
    // embex.createComponent(componentId, data.typeId, data.values);
    
    return true;
  }
  
  // ==========================================================================
  // HELPERS
  // ==========================================================================
  
  /**
   * Get default value for field type
   */
  private getDefaultForType(type: FieldType): unknown {
    switch (type) {
      case 'bool': return false;
      case 'i8': case 'i16': case 'i32': case 'i64':
      case 'u8': case 'u16': case 'u32': case 'u64':
      case 'f32': case 'f64': return 0;
      case 'string': return '';
      case 'vec2': return { x: 0, y: 0 };
      case 'vec3': return { x: 0, y: 0, z: 0 };
      case 'vec4': return { x: 0, y: 0, z: 0, w: 0 };
      case 'color': return { r: 1, g: 1, b: 1, a: 1 };
      case 'color32': return { r: 255, g: 255, b: 255, a: 255 };
      case 'entity': return null;
      case 'asset': return null;
      case 'array': return [];
      case 'object': return {};
      default: return null;
    }
  }
}

// Export singleton accessor
export const componentRegistry = ComponentRegistry.instance;
